import type { MapDef } from './MapDef.js';

// V1.5+ third polished map — "Canyon".
//
// Identity (vs Foundry / Vault):
//   - Foundry: straight north/south corridor, three relays on the same
//     vertical centre line. Head-on contest for every relay.
//   - Vault:   S-curve relay chain, two lanes that flank mid from left
//     or right of a single corridor.
//   - Canyon:  Z-zigzag relay chain. The north relay sits on the LEFT
//     of centre; the south relay sits on the RIGHT. The path from Core
//     to Core runs diagonally LEFT → CENTRE → RIGHT, creating two
//     distinct flanking corridors (a west lane hugging the left edge and
//     an east lane hugging the right edge) plus a diagonal "crossing"
//     zone between north-relay and south-relay. Squads must cross the
//     map laterally to follow the signal chain, so positional play and
//     lane switches matter more than in Foundry / Vault.
//
// Map invariants (same as all V1.5+ maps):
//   - 2000 × 2000 world, 32-px tile.
//   - Two cores, three control nodes (north / mid / south).
//   - Signal-link chain viable end-to-end (each adjacent pair ≤ 500 px):
//       Core 0 (1000, 220)  ↔ north (700, 600):  √(300²+380²) ≈ 484 ✓
//       north (700, 600)    ↔ mid   (1000, 1000): √(300²+400²)  = 500 ✓
//       mid   (1000, 1000)  ↔ south (1300, 1400): √(300²+400²)  = 500 ✓
//       south (1300, 1400)  ↔ Core 1 (1000, 1780):√(300²+380²) ≈ 484 ✓
//     Core ↔ mid distance is 780 px > 500 — mid must be reached by
//     hopping through a near relay (preserves the §11 design intent).
//   - Camp anchor at mid relay (1000, 1000) — CampSystem unchanged.
//
// Authoring rules (same as Foundry / Vault):
//   - Stay clear of the 120 px capture radius of every relay.
//   - Stay clear of the 200 px build-slot ring around every Core.
//   - Stay clear of squad spawn points.
//   - No obstacle intrudes closer than CACHE_PICKUP_RADIUS_PX − 10 = 80 px
//     from the mid-relay / camp anchor.
//   - All obstacles are positive-area rects.
export const CANYON: MapDef = {
  id: 'canyon',
  name: 'Canyon',
  width: 2000,
  height: 2000,
  tilePx: 32,
  cores: [
    { ownerId: 0, pos: { x: 1000, y: 220 } },
    { ownerId: 1, pos: { x: 1000, y: 1780 } },
  ],
  controlNodes: [
    // Z-zigzag: north relay is LEFT of centre, south relay RIGHT.
    // Signal chain runs Core 0 → NW → Mid → SE → Core 1.
    { name: 'north', pos: { x: 700, y: 600 } },
    { name: 'mid',   pos: { x: 1000, y: 1000 } },
    { name: 'south', pos: { x: 1300, y: 1400 } },
  ],
  obstacles: [
    // --- Core 0 channel walls ---
    // Channel squads away from a straight south push and toward the left-
    // side path that reaches the north relay.  Both walls verified > 200 px
    // from Core 0 (1000, 220) and > 120 px from north relay (700, 600).
    { x: 700, y: 400, w: 100, h: 60 },   // left-channel wall  (dist to north relay ≈ 140 px ✓)
    { x: 1200, y: 400, w: 100, h: 60 },  // right-block wall   (dist to Core 0 ≈ 283 px ✓)

    // --- Core 1 channel walls (180° rotational mirror) ---
    { x: 700, y: 1540, w: 100, h: 60 },  // left-block wall    (dist to Core 1 ≈ 269 px ✓)
    { x: 1200, y: 1540, w: 100, h: 60 }, // right-channel wall (dist to south relay ≈ 140 px ✓)

    // --- Mid approach walls ---
    // Force squads coming from the north relay to commit to a side before
    // entering the mid-relay capture circle.  All > 120 px from mid and
    // > 80 px from the camp-pickup radius.
    { x: 660, y: 860, w: 120, h: 60 },   // left wall  (dist to mid ≈ 234 px ✓)
    { x: 1220, y: 1080, w: 120, h: 60 }, // right wall (dist to mid ≈ 234 px ✓)

    // --- Mid flanking blocks ---
    // Smaller rocks that prevent a fast flanking charge from immediately
    // hugging the relay while still leaving the capture radius open.
    { x: 820, y: 860, w: 60, h: 60 },    // dist to mid ≈ 144 px ✓
    { x: 1120, y: 1080, w: 60, h: 60 },  // dist to mid ≈ 144 px ✓

    // --- Relay cover walls ---
    // Give squads a piece of cover after capturing a relay so they can
    // defend without standing fully exposed.
    { x: 500, y: 700, w: 80, h: 50 },    // shelter west of north relay  (dist ≈ 156 px ✓)
    { x: 1420, y: 1250, w: 80, h: 50 },  // shelter east of south relay  (dist ≈ 156 px ✓)

    // --- Engineer mini-turret nests (sideline positions) ---
    // Snug against the map edge, well away from any relay or build ring,
    // giving engineers a forward-fortification option on the flanks.
    { x: 440, y: 560, w: 60, h: 60 },    // upper-left  nest (dist to north relay = 200 px ✓)
    { x: 1500, y: 1380, w: 60, h: 60 },  // lower-right nest (dist to south relay = 200 px ✓)
  ],
  spawns: [
    { ownerId: 0, pos: { x: 920, y: 300 } },
    { ownerId: 1, pos: { x: 1080, y: 1700 } },
  ],
  // Mid relay is the V1.5 neutral camp anchor — same as Foundry and Vault.
  neutralCampAnchor: { x: 1000, y: 1000 },
};
